import { Typography } from 'antd';
import { StyledStack } from 'app/components/Stack';
import StyledPagination from 'app/components/StyledPagination';
import StyledTable from 'app/components/StyledTable';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import useFetchData from 'app/hooks/useFetchData';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import RangeDatePickerCustom from 'app/pages/NewAccount/CheckTickets/RangeDatePickerCustom';
import useGetDetailTicket from 'app/pages/NewAccount/hooks/useGetDetailTicket';
import { getTransactionHistoryApi } from 'app/services/agentApi';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { formatNumber } from 'utils/helper';
import { PageStateContext } from '..';
import Filter, {
  createDateObjectByTimezone,
  FilterFormValues,
  MAIN_TABS,
  transformFilterValues,
} from './Filter';
import { RangeDatePickerContainer, SmallTitle } from './OverviewScreen';
import { useHistory } from 'react-router-dom';

const TransactionList: React.FC = () => {
  const { getValues, setValue, reset } = useFormContext<FilterFormValues>();
  const { tableRef, setScreen, setPageLoading, anchorPointerRef, filterRef } =
    useContext(PageStateContext);
  const translations = useSelector(selectTranslations);
  const { result: overviewData, onFetchData: onFetchOverviewData } =
    useFetchData({
      actionApi: getTransactionHistoryApi,
      IIFE: false,
      onBefore: () => setPageLoading(true),
      onFinally: () => setPageLoading(false),
      payloadData: {
        dateFrom: getValues('dateFromList')?.format('DD/MM/YYYY'),
        dateTo: (getValues('dateToList') ?? getValues('dateFromList'))?.format(
          'DD/MM/YYYY',
        ),
        documentNumber: getValues('documentNumber') ?? '',
        transactionType: getValues('transactionType') ?? '',
        paymentMethod: getValues('paymentMethod') ?? '',
        page: getValues('page'),
        limit: getValues('limit'),
        typeReport: getValues('typeReport'),
        isHoliday: getValues('productType') ?? '',
      },
    });
  useEffect(() => {
    onFetchOverviewData({
      callback: {
        onFinally: () => {
          setPageLoading(false);
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { onClickShowDetail, isGetingDetail } = useGetDetailTicket({
    screen: '/agent/transaction-history?screen=transaction-list',
    setIsLoading: setPageLoading,
  });
  const history = useHistory();
  const onClickShowDetailHoliday = (orderCode: string) => {
    history.push(`/holiday/order-detail/${orderCode}`);
  };
  const dataSource = useMemo(() => {
    const details = overviewData?.details || [];
    const paginator = overviewData?.paginator || {};
    return details.map((item, index) => ({
      order: (paginator?.current - 1) * paginator?.numItemsPerPage + index + 1,
      created_at: item.created_at,
      documentNumber: item?.documentNumber,
      orderCode: item?.submitNumber,
      rootOrder: item?.original_order,
      transactionType: item?.transaction_type_name,
      transactionName: item?.so_type_name,
      pttt: item?.payMethod,
      paymentMethodRoot: item?.original_payMethod,
      amountPayment: +item?.amount,
      penaltyFee: item?.penalty_amount,
      isOrder: item?.isOrder || false,
      invoices: item?.invoices || {},
      isHoliday: item?.isHoliday || false,
    }));
  }, [overviewData?.details, overviewData?.paginator]);

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.txt.agent-table-order',
      'b2b.txt.time',
      'b2b.txt.order-id',
      'b2b.txt.order-code',
      'b2b.txt.root-order',
      'b2b.txt.transaction-type',
      'b2b.txt.transaction_name',
      'b2c.txt.pttt',
      'b2b.txt.payment-method-root',
      'b2b.txt.amount-payment',
      'b2b.txt.penaltyFee',
      'b2b.txt.productType',
      'b2b.txt.invoiceNumber',
    ],
  });

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.agent-table-order']}
          </div>
        ),
        key: 'order',
        dataIndex: 'order',
        render: value => {
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.agent-table-order'],
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.time']}
          </div>
        ),
        dataIndex: 'created_at',
        key: 'created_at',
        render: value => {
          return (
            <StyledStack
              justifyContent={'start'}
              style={{ minWidth: titleColumnSizes['b2b.txt.time'] }}
            >
              <div style={{ textAlign: 'left' }}>
                <Typography>{value}</Typography>
              </div>
            </StyledStack>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-id']}
          </div>
        ),
        dataIndex: 'documentNumber',
        key: 'documentNumber',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-id'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.order-code']}
          </div>
        ),
        dataIndex: 'orderCode',
        key: 'orderCode',
        render: (value, record) => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.order-code'],
              ...(record?.isOrder
                ? { color: '#F8B117', cursor: 'pointer' }
                : {}),
            }}
            onClick={() =>
              record?.isHoliday
                ? onClickShowDetailHoliday(value)
                : onClickShowDetail(value)
            }
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.root-order']}
          </div>
        ),
        dataIndex: 'rootOrder',
        key: 'rootOrder',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.root-order'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction-type']}
          </div>
        ),
        dataIndex: 'transactionType',
        key: 'transactionType',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction-type'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.transaction_name']}
          </div>
        ),
        dataIndex: 'transactionName',
        key: 'transactionName',
        render: value => (
          <div
            style={{
              textAlign: 'left',
              minWidth: titleColumnSizes['b2b.txt.transaction_name'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2c.txt.pttt']}
          </div>
        ),
        dataIndex: 'pttt',
        key: 'pttt',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2c.txt.pttt'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.payment-method-root']}
          </div>
        ),
        dataIndex: 'paymentMethodRoot',
        key: 'paymentMethodRoot',
        render: value => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2b.txt.payment-method-root'],
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.amount-payment']}
          </div>
        ),
        dataIndex: 'amountPayment',
        key: 'amountPayment',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.amount-payment'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            {translations?.['b2b.txt.penaltyFee']}
          </div>
        ),
        dataIndex: 'penaltyFee',
        key: 'penaltyFee',
        render: value => (
          <div
            style={{
              textAlign: 'right',
              minWidth: titleColumnSizes['b2b.txt.penaltyFee'],
            }}
          >
            {_.isNil(value)
              ? null
              : `${formatNumber(value).replaceAll(',', '.')}đ`}
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.productType']}
          </div>
        ),
        dataIndex: 'productType',
        key: 'productType',
        render: (value, record) => (
          <div
            style={{
              textAlign: 'center',
              minWidth: titleColumnSizes['b2b.txt.productType'],
            }}
          >
            {
              translations?.[
                record.isHoliday
                  ? 'b2b.txt.holidayComboTicket'
                  : 'b2b.txt.parkTicket'
              ]
            }
          </div>
        ),
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.invoiceNumber']}
          </div>
        ),
        dataIndex: 'invoices',
        key: 'invoices',
        render: value => {
          const invoiceList = (value?.seq || '')
            .toString()
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
          return (
            <div
              style={{
                textAlign: 'left',
                minWidth: titleColumnSizes['b2b.txt.invoiceNumber'],
                whiteSpace: 'pre-line',
              }}
            >
              {invoiceList.map((inv, index) => (
                <div key={index}>{inv}</div>
              ))}
            </div>
          );
        },
      },
    ],
    [titleColumnSizes, translations],
  );
  const handleChangePage = async (page: number, pageSize: number) => {
    setValue('page', page);
    setValue('limit', pageSize);

    filterRef.current = {
      ...filterRef.current,
      page,
      limit: pageSize,
    };

    onFetchOverviewData({
      payloadData: transformFilterValues({
        ...filterRef.current,
        page,
        limit: pageSize,
      }),
      callback: {
        onSuccess: () => {
          if (anchorPointerRef?.current) {
            const top =
              anchorPointerRef.current.getBoundingClientRect().top +
              window.scrollY -
              130;
            window.scrollTo({ top, behavior: 'smooth' });
          }

          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };
  const handleChangeTime = () => {
    const isSameDateFrom = filterRef.current.dateFromList?.isSame(
      getValues('dateFromList'),
      'day',
    );

    const isSameDateTo = filterRef.current.dateToList?.isSame(
      getValues('dateToList') ?? getValues('dateFromList'),
      'day',
    );

    if (isSameDateFrom && isSameDateTo) return;

    setValue('page', 1);

    filterRef.current = {
      ...filterRef.current,
      dateFromList: getValues('dateFromList'),
      dateToList: getValues('dateToList') ?? getValues('dateFromList'),
      page: 1,
      tab: MAIN_TABS.TRANSACTION_LIST,
    };
    onFetchOverviewData({
      payloadData: transformFilterValues(filterRef.current),
      callback: {
        onBefore: () => {
          setPageLoading(true);
        },
        onSuccess: () => {
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
        onFinally: () => {
          setPageLoading(false);
        },
      },
    });
  };
  return (
    <StyledStack rowGap={'24px'} direction={'column'}>
      <Typography
        style={{
          fontWeight: 600,
          fontSize: 20,
          color: 'black',
          textTransform: 'uppercase',
        }}
      >
        {translations?.['b2b.txt.transactionList'] ?? ''}
      </Typography>
      <div className="d-flex align-items-end" style={{ width: '100%' }}>
        <RangeDatePickerContainer>
          <SmallTitle>
            {translations?.['b2b.txt.transactionDate'] ?? ''}
          </SmallTitle>
          <RangeDatePickerCustom
            placeholder={
              translations ? translations['b2b.txt.ticket-expried'] : ''
            }
            startDateInit={createDateObjectByTimezone(
              getValues('dateFromList'),
            )}
            endDateInit={createDateObjectByTimezone(getValues('dateToList'))}
            onChange={([start, end]) => {
              setValue('dateFromList', start ? moment(start) : null);
              setValue('dateToList', end ? moment(end) : null);
            }}
            minDate={null}
            onCalendarClose={handleChangeTime}
          />
        </RangeDatePickerContainer>
      </div>
      <div ref={anchorPointerRef} style={{ width: '100%' }}>
        <Filter
          onSearch={onFetchOverviewData}
          disabledExportExcel={!dataSource?.length}
        />
      </div>

      <div style={{ width: '100%' }}>
        <StyledTable
          tableRef={tableRef}
          dataSource={dataSource ?? []}
          columns={columns}
          tableProps={{
            pagination: false,
            scroll: {
              x: dataSource?.length ? 'auto' : 'max-content',
              y: dataSource?.length ? '50vh' : undefined,
            },
          }}
          styleSlots={{
            antTableContainer: {
              overflow: 'hidden',
            },
            antTableHeader: {
              border: 'none !important',
              borderRadius: '0 !important',
            },
            tableCSS: {
              borderTopLeftRadius: !dataSource?.length ? '' : '0 !important',
              borderTopRightRadius: !dataSource?.length ? '' : '0 !important',
            },
            antTableBody: {
              paddingBottom: 10,
            },
          }}
        />
        {overviewData?.paginator && (
          <>
            <StyledPagination
              showSizeChanger
              style={{ margin: 'auto', marginTop: 24 }}
              current={getValues('page') || 1}
              total={overviewData?.paginator?.totalCount || 0}
              pageSize={getValues('limit') || 20}
              onChange={handleChangePage}
              pageSizeOptions={['20', '50', '100']}
            />
          </>
        )}
      </div>
    </StyledStack>
  );
};

export default TransactionList;
